/**
 * Shared, framework-free import mapping/validation logic used by
 * ImportCSVModal (via webpack CJS interop) AND by the Node test harness
 * (scripts/test-harness/importMatrix.js), so tests exercise the exact
 * production pipeline instead of a reimplementation.
 *
 * Deliberately CommonJS with no React/Chakra/browser imports.
 */

// RETR CSV format detection: checks if headers match RETR's known column structure
const RETR_SIGNATURE_HEADERS = ['LO Name', 'Loan Ct', 'Loan Vol', 'Profile URL'];
const RETR_EXPANDED_SIGNATURE_HEADERS = ['retr_profile_url', 'retr_profile_id', 'nmls_id', 'licensed_since'];

function detectRetrFormat(headers) {
  const headerSet = new Set(headers.map(h => h.trim()));
  const classicMatches = RETR_SIGNATURE_HEADERS.filter(sig => headerSet.has(sig));
  if (classicMatches.length >= 3) return true;
  const expandedMatches = RETR_EXPANDED_SIGNATURE_HEADERS.filter(sig => headerSet.has(sig));
  return expandedMatches.length >= 2;
}

// Parse RETR currency strings: "$400,021,141" -> 400021141, "$370K" -> 370000
function parseRetrCurrency(value) {
  if (!value || typeof value !== 'string') return null;
  const cleaned = value.replace(/[$,\s]/g, '');
  if (!cleaned) return null;
  const upperCleaned = cleaned.toUpperCase();
  if (upperCleaned.endsWith('K')) {
    const num = parseFloat(upperCleaned.replace('K', ''));
    return isNaN(num) ? null : Math.round(num * 1000);
  }
  if (upperCleaned.endsWith('M')) {
    const num = parseFloat(upperCleaned.replace('M', ''));
    return isNaN(num) ? null : Math.round(num * 1000000);
  }
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : Math.round(num);
}

// Parse RETR integer strings: "1,077" -> 1077
function parseRetrInt(value) {
  if (!value || typeof value !== 'string') return null;
  const cleaned = value.replace(/[,\s]/g, '');
  const num = parseInt(cleaned, 10);
  return isNaN(num) ? null : num;
}

// Split full name into first + last: "Matthew Weaver" -> { first: "Matthew", last: "Weaver" }
function splitFullName(fullName) {
  if (!fullName || typeof fullName !== 'string') return { first: '', last: '' };
  const trimmed = fullName.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

// Fields each backend import function actually consumes. A column mapped to a
// field the backend ignores would be silently dropped (it is excluded from
// extraFields because it counts as "mapped"), so auto-detection is filtered to
// these and anything else is routed into extra_fields instead.
const CONTACT_IMPORT_CONSUMED_FIELDS = [
  'firstName', 'lastName', 'middleInitial', 'email', 'phone',
  'secondaryEmail', 'secondaryPhone', 'preferredContactMethod',
  'company', 'jobTitle', 'licenseNumber', 'licenseState', 'website',
  'officeAddress', 'contactTypes', 'tags', 'source', 'notes',
  'birthday', 'mailingStreet', 'mailingCity', 'mailingState', 'mailingZip',
];
const DEAL_IMPORT_CONSUMED_FIELDS = [
  ...CONTACT_IMPORT_CONSUMED_FIELDS,
  'product', 'amount', 'loanAmount', 'loanNumber', 'loanTerm', 'interestRate',
  'lender', 'loanPurpose', 'purchaseOrRefi', 'creditScore', 'downPayment',
  'occupancyType', 'stage', 'estimatedClosingDate', 'appDate',
  'propertyAddress', 'propertyCity', 'propertyState', 'propertyZip', 'propertyType',
  'buyerAgentName', 'buyerAgentPhone', 'buyerAgentEmail', 'buyerAgentCompany',
  'sellerAgentName', 'sellerAgentPhone', 'sellerAgentEmail', 'sellerAgentCompany',
  'titleCompanyName', 'titleCompanyPhone', 'titleCompanyEmail', 'titleCompanyContact',
];

function isFieldConsumed(field, isRetr, importType) {
  if (isRetr) {
    return field === 'loName' || field.startsWith('retr') ||
      CONTACT_IMPORT_CONSUMED_FIELDS.includes(field);
  }
  return importType === 'deals'
    ? DEAL_IMPORT_CONSUMED_FIELDS.includes(field)
    : CONTACT_IMPORT_CONSUMED_FIELDS.includes(field);
}

// Header-variation dictionary for auto-detection
const FIELD_VARIATIONS = {
  // Basic Info
  firstName: ['firstName', 'first_name', 'First Name', 'first', 'fname', 'givenname'],
  lastName: ['lastName', 'last_name', 'Last Name', 'last', 'lname', 'surname', 'familyname'],
  middleInitial: ['middleInitial', 'middle_initial', 'Middle Initial', 'MI', 'mi'],

  // Contact Details
  email: ['email', 'Email', 'emailAddress', 'email_address', 'Email Address', 'e-mail'],
  phone: ['phone', 'Phone', 'phoneNumber', 'phone_number', 'Phone Number', 'telephone', 'mobile', 'cell'],
  secondaryEmail: ['secondaryEmail', 'secondary_email', 'Secondary Email', 'alt_email', 'Alt Email'],
  secondaryPhone: ['secondaryPhone', 'secondary_phone', 'Secondary Phone', 'alt_phone', 'Alt Phone'],
  preferredContactMethod: ['preferredContactMethod', 'preferred_method', 'Preferred Method', 'contact_method'],

  // Professional Info
  company: ['company', 'Company', 'organization', 'Organization', 'business', 'brokerage', 'Brokerage', 'Firm', 'firm', 'Office', 'office'],
  jobTitle: ['jobTitle', 'job_title', 'Job Title', 'title', 'Title', 'position', 'Role', 'role'],
  licenseNumber: ['licenseNumber', 'license_number', 'License Number', 'License #', 'license', 'bre', 'dre', 'nmls'],
  licenseState: ['licenseState', 'license_state', 'License State', 'state_license'],
  website: ['website', 'Website', 'url', 'URL', 'Web'],
  officeAddress: ['officeAddress', 'office_address', 'Office Address', 'Business Address', 'address'],

  // Classification (contactTypes is defined further down, after
  // purchaseOrRefi, so bare "Type" headers resolve by import kind)
  tags: ['tags', 'Tags', 'categories', 'labels'],
  source: ['source', 'Source', 'leadSource', 'lead_source', 'Lead Source'],

  // Deal Specific
  product: ['product', 'Product', 'loanType', 'loan_type', 'Loan Type', 'loanProduct'],
  amount: ['amount', 'Amount', 'loanAmount', 'loan_amount', 'Loan Amount', 'price'],
  notes: ['notes', 'Notes', 'comments', 'description', 'Description'],
  propertyAddress: ['propertyAddress', 'property_address', 'Property Address'],
  propertyType: ['propertyType', 'property_type', 'Property Type'],
  creditScore: ['creditScore', 'credit_score', 'Credit Score', 'fico', 'FICO'],
  downPayment: ['downPayment', 'down_payment', 'Down Payment', 'dp'],
  loanPurpose: ['loanPurpose', 'loan_purpose', 'Loan Purpose', 'purpose'],
  purchaseOrRefi: ['purchaseOrRefi', 'purchase_or_refi', 'Transaction Type', 'transactionType', 'type'],
  // Defined AFTER purchaseOrRefi on purpose: both claim a bare "Type" header,
  // and with type-aware detection the winner follows the import kind — deals
  // imports keep Type -> purchaseOrRefi (defined first), contacts imports skip
  // purchaseOrRefi (unconsumed) so Type lands on contactTypes. Homebot-style
  // contact exports use exactly this shape (Type = Contact / RE Agent).
  contactTypes: ['contactTypes', 'contact_types', 'Contact Type', 'Contact Role', 'User Type', 'Type'],
  loanNumber: ['loanNumber', 'loan_number', 'Loan Number', 'Loan #'],
  stage: ['stage', 'Stage', 'deal_stage', 'Deal Stage', 'pipeline_stage', 'Pipeline Stage', 'status', 'Status', 'Deal Status', 'Loan Status'],
  estimatedClosingDate: ['estimatedClosingDate', 'estimated_closing_date', 'Estimated Closing Date', 'Est. Closing Date', 'est_close_date', 'Est Close Date', 'closing_date', 'Closing Date', 'close_date', 'Close Date', 'closeDate', 'COE Date'],
  appDate: ['appDate', 'app_date', 'App Date', 'application_date', 'Application Date', 'Applied Date', 'Loan App Date'],
  loanTerm: ['loanTerm', 'loan_term', 'Loan Term', 'Term'],
  interestRate: ['interestRate', 'interest_rate', 'Interest Rate', 'Rate'],
  loanAmount: ['loanAmount', 'loan_amount', 'Loan Amount'],
  lender: ['lender', 'Lender', 'lender_name', 'Lender Name'],
  occupancyType: ['occupancyType', 'occupancy_type', 'Occupancy Type', 'Occupancy'],
  propertyCity: ['propertyCity', 'property_city', 'Property City', 'City'],
  propertyState: ['propertyState', 'property_state', 'Property State', 'State'],
  propertyZip: ['propertyZip', 'property_zip', 'Property Zip', 'Zip', 'ZIP'],
  buyerAgentName: ['buyerAgentName', 'buyer_agent_name', 'Buyer Agent', 'Buyer Agent Name'],
  buyerAgentPhone: ['buyerAgentPhone', 'buyer_agent_phone', 'Buyer Agent Phone'],
  buyerAgentEmail: ['buyerAgentEmail', 'buyer_agent_email', 'Buyer Agent Email'],
  buyerAgentCompany: ['buyerAgentCompany', 'buyer_agent_company', 'Buyer Agent Company'],
  sellerAgentName: ['sellerAgentName', 'seller_agent_name', 'Listing Agent', 'Seller Agent', 'Listing Agent Name'],
  sellerAgentPhone: ['sellerAgentPhone', 'seller_agent_phone', 'Listing Agent Phone', 'Seller Agent Phone'],
  sellerAgentEmail: ['sellerAgentEmail', 'seller_agent_email', 'Listing Agent Email', 'Seller Agent Email'],
  sellerAgentCompany: ['sellerAgentCompany', 'seller_agent_company', 'Listing Agent Company', 'Seller Agent Company'],
  titleCompanyName: ['titleCompanyName', 'title_company_name', 'Title Company', 'Title Company Name'],
  titleCompanyPhone: ['titleCompanyPhone', 'title_company_phone', 'Title Company Phone'],
  titleCompanyEmail: ['titleCompanyEmail', 'title_company_email', 'Title Company Email'],
  titleCompanyContact: ['titleCompanyContact', 'title_company_contact', 'Title Contact', 'Title Company Contact'],

  // Contact personal fields. NOTE: these are defined AFTER the property*
  // fields on purpose — for type-aware detection (see detectColumnMapping),
  // generic "City"/"State"/"Zip" headers resolve to property fields on deals
  // imports (where both are consumed) and to mailing fields on contacts
  // imports (where property fields are filtered out).
  birthday: ['birthday', 'Birthday', 'birth_date', 'Birth Date', 'birthdate', 'date_of_birth', 'Date of Birth', 'DOB', 'dob'],
  mailingStreet: ['mailingStreet', 'mailing_street', 'Mailing Street', 'Mailing Address', 'Street Address', 'Home Address', 'Address 1', 'Address1', 'street'],
  mailingCity: ['mailingCity', 'mailing_city', 'Mailing City', 'Home City', 'City', 'city'],
  mailingState: ['mailingState', 'mailing_state', 'Mailing State', 'Home State', 'State', 'state'],
  mailingZip: ['mailingZip', 'mailing_zip', 'Mailing Zip', 'Home Zip', 'Zip', 'ZIP', 'Zip Code', 'zip_code', 'Postal Code', 'postal_code'],

  // RETR-specific fields (LO Name handled separately via loName -> firstName/lastName split)
  loName: ['LO Name', 'lo_name', 'Loan Officer Name', 'LO', 'full_name'],
  retrProfileUrl: ['Profile URL', 'profile_url', 'retr_profile_url', 'RETR URL', 'Profile Link'],
  retrLoType: ['LO Type', 'lo_type'],
  retrLoanCount: ['Loan Ct', 'loan_count', 'Loan Count', 'State Loan Count'],
  retrLoanVolume: ['Loan Vol', 'loan_volume', 'Loan Volume', 'State Loan Volume'],
  retrConvCount: ['Conv Ct', 'conv_count', 'Conventional Count', 'Conv Count'],
  retrVaCount: ['VA Ct', 'va_count', 'VA Count'],
  retrFhaCount: ['FHA Ct', 'fha_count', 'FHA Count'],
  retrTotalLoanCount: ['Loan Ct (Total)', 'total_loan_count', 'Total Loan Count', 'Nationwide Loan Count', 'top_loan_count'],
  retrTotalLoanVolume: ['Loan Vol (Total)', 'total_loan_volume', 'Total Loan Volume', 'Nationwide Volume', 'top_loan_volume'],
  retrAvgLoan: ['Avg Loan', 'avg_loan', 'Average Loan', 'Avg Loan Size'],
  retrLocation: ['LO Location', 'lo_location', 'Location'],

  // RETR expanded fields
  retrProfileId: ['Profile ID', 'profile_id', 'retr_profile_id', 'LoanOfficerID', 'RETR ID'],
  retrNmlsId: ['NMLS ID', 'NMLSID', 'nmls_id', 'NMLS', 'NMLS #'],
  retrLicensedSince: ['Licensed Since', 'licensed_since', 'License Year'],
  retrJobTitle: ['LO Title', 'Job Title', 'Role', 'Title', 'job_title'],
  retrWorkEmail: ['Work Email', 'WorkEmail', 'work_email', 'Business Email'],
  retrPersonalEmail: ['Personal Email', 'PersonalEmail', 'personal_email'],
  retrOfficePhone: ['Office Phone', 'OfficePhone', 'office_phone', 'Work Phone'],
  retrMobilePhone: ['Mobile Phone', 'MobilePhone', 'mobile_phone', 'Cell Phone', 'Cell'],
  retrLinkedinUrl: ['LinkedIn', 'LinkedIn URL', 'linkedin_url'],
  retrFacebookUrl: ['Facebook', 'Facebook URL', 'facebook_url'],
  retrTwitterUrl: ['Twitter', 'Twitter URL', 'twitter_url'],
  retrInstagramUrl: ['Instagram', 'Instagram URL', 'instagram_url'],
  retrZillowUrl: ['Zillow URL', 'ZillowUrl', 'zillow_url'],
  retrWebsiteUrl: ['Website URL', 'Website', 'website_url'],
  retrExperienceUrl: ['Experience URL', 'ExperienceURL', 'experience_url', 'Resume URL'],
  retrGoogleSearchUrl: ['Google Search URL', 'google_search_url'],
  retrFacebookSearchUrl: ['Facebook Search URL', 'facebook_search_url'],
  retrLinkedinSearchUrl: ['LinkedIn Search URL', 'linkedin_search_url'],
  retrZillowSearchUrl: ['Zillow Search URL', 'zillow_search_url'],
  retrNmlsLookupUrl: ['NMLS Lookup URL', 'nmls_lookup_url', 'nmls_consumer_access_url', 'NMLS Consumer Access'],
  retrCompanyName: ['Current Company', 'current_company', 'Employer', 'Company Name', 'Employer Name'],
  retrBranchUrl: ['Branch URL', 'Branch Profile URL', 'branch_url', 'branch_profile_url'],
  retrOfficeLocation: ['Office City State', 'Office Location', 'office_location', 'Office City State ZIP'],
  retrEmployerStartDate: ['Employer Start Date', 'Start Date at Employer', 'employer_start_date', 'start_date_current_employer'],
  retrBuyerAgentsCount: ['Buyer Agents', 'buyer_agents', 'Buyer Agents Count', 'top_buyer_agents'],
  retrYearsExperience: ['Years Experience', 'years_experience', 'Years of Experience', 'Experience Years', 'top_years_experience'],
  retrStateLicensesCount: ['State Licenses', 'state_licenses_count', 'State Licenses Count', 'top_state_licenses'],
  retrFederalRegistrationsCount: ['Federal Registrations', 'federal_registrations_count', 'top_federal_registrations'],
  retrTrackRecordKey: ['Track Record Key', 'track_record_key'],
  retrHasPersonality: ['Has Personality', 'has_personality', 'has_personality_assessment'],
};

/**
 * Auto-detect column mappings from raw headers.
 *
 * When `options.importType` is passed, fields the import type doesn't consume
 * are skipped DURING matching, so an ambiguous header falls through to the
 * consumable field instead (e.g. "City" -> propertyCity on deals imports,
 * mailingCity on contacts imports). Without options the legacy first-match
 * behavior is preserved.
 */
function detectColumnMapping(headers, options = {}) {
  const { importType, isRetrImport } = options;
  const typeAware = importType !== undefined;
  const mapping = {};
  headers.forEach((header) => {
    const headerLower = header.toLowerCase().trim();
    for (const [fieldName, variations] of Object.entries(FIELD_VARIATIONS)) {
      if (typeAware && !isFieldConsumed(fieldName, !!isRetrImport, importType)) continue;
      const found = variations.some(variation =>
        variation.toLowerCase() === headerLower ||
        header.trim() === variation
      );
      if (found) {
        mapping[fieldName] = header;
        return;
      }
    }
  });
  return mapping;
}

// ------------------------------------------------------- import kind detection

const DEAL_ONLY_FIELDS = new Set(
  DEAL_IMPORT_CONSUMED_FIELDS.filter(f => !CONTACT_IMPORT_CONSUMED_FIELDS.includes(f))
);

// Headers too generic to prove the file is a deals list on their own
// ("Status", "Type", "Price"... appear in plenty of plain contact exports).
const GENERIC_DEAL_HEADERS = new Set([
  'status', 'type', 'price', 'rate', 'term', 'city', 'state', 'zip', 'purpose', 'product',
]);

/**
 * Classify an uploaded file as a contacts list or a deals list from its
 * headers alone — people regularly open the importer from the "wrong" page,
 * so the entry point can't be trusted as the source of truth.
 *
 * A file is a deals list when it has at least one UNAMBIGUOUS deal column
 * (Loan Number, Est Close Date, Buyer Agent Phone...). Generic headers that
 * merely *can* map to deal fields ("Status", "City") never flip the answer.
 *
 * @returns {{ kind: 'contacts'|'deals', confidence: 'high'|'medium',
 *   dealHeaders: string[] }} dealHeaders lists the strong-signal headers.
 */
function detectImportKind(headers) {
  const strongHeaders = [];
  let weakCount = 0;

  (headers || []).forEach((header) => {
    const headerLower = String(header).toLowerCase().trim();
    for (const [fieldName, variations] of Object.entries(FIELD_VARIATIONS)) {
      const found = variations.some(variation =>
        variation.toLowerCase() === headerLower || String(header).trim() === variation
      );
      if (!found) continue;
      if (DEAL_ONLY_FIELDS.has(fieldName)) {
        if (GENERIC_DEAL_HEADERS.has(headerLower)) weakCount += 1;
        else strongHeaders.push(header);
      }
      return;
    }
  });

  if (strongHeaders.length > 0) {
    return {
      kind: 'deals',
      confidence: strongHeaders.length >= 2 ? 'high' : 'medium',
      dealHeaders: strongHeaders,
    };
  }
  return { kind: 'contacts', confidence: weakCount > 0 ? 'medium' : 'high', dealHeaders: [] };
}

/**
 * Pure validation core. fileErrors block the whole import (bad mapping);
 * rowErrors mark individual rows that can be skipped with "Import valid
 * rows only". Returns un-truncated lists; the UI truncates for display.
 */
function validateMappedData({ csvData, columnMapping, isRetrImport, retrState }) {
  const fileErrors = [];
  const rowErrors = [];
  const warnings = [];
  const badRowIndexes = new Set();

  const markRowError = (index, message) => {
    rowErrors.push(message);
    badRowIndexes.add(index);
  };

  if (isRetrImport) {
    // RETR validation: require either loName or firstName+lastName
    const hasLoName = !!columnMapping.loName;
    const hasFirstLast = !!columnMapping.firstName && !!columnMapping.lastName;
    if (!hasLoName && !hasFirstLast) {
      fileErrors.push('RETR import requires "LO Name" or both "firstName" and "lastName" to be mapped');
    }

    // RETR requires state selection
    if (!retrState) {
      fileErrors.push('Please select the state this RETR data covers');
    }

    if (fileErrors.length === 0 && csvData && csvData.length > 0) {
      csvData.forEach((row, index) => {
        const rowNum = index + 2;
        if (hasLoName) {
          const name = row[columnMapping.loName];
          if (!name || !String(name).trim()) {
            markRowError(index, `Row ${rowNum}: Missing LO Name`);
          }
        } else {
          if (!row[columnMapping.firstName]) markRowError(index, `Row ${rowNum}: Missing firstName`);
          if (!row[columnMapping.lastName]) markRowError(index, `Row ${rowNum}: Missing lastName`);
        }
      });
    }
  } else {
    // Standard validation
    const requiredFields = ['firstName', 'lastName'];
    requiredFields.forEach(field => {
      if (!columnMapping[field]) {
        fileErrors.push(`Required field "${field}" is not mapped`);
      }
    });

    if (!columnMapping.email && !columnMapping.phone) {
      fileErrors.push('Either "email" or "phone" must be mapped');
    }

    if (fileErrors.length === 0 && csvData && csvData.length > 0) {
      csvData.forEach((row, index) => {
        const rowNum = index + 2;
        const fn = columnMapping.firstName ? (row[columnMapping.firstName] || '') : '';
        const ln = columnMapping.lastName ? (row[columnMapping.lastName] || '') : '';
        const em = columnMapping.email ? (row[columnMapping.email] || '') : '';
        const ph = columnMapping.phone ? (row[columnMapping.phone] || '') : '';
        const nameLabel = (fn || ln) ? `${fn} ${ln}`.trim() : '';
        const ident = nameLabel || em || ph || 'unknown';

        if (columnMapping.firstName && !fn) {
          markRowError(index, `Row ${rowNum}: Missing first name — ${ln || em || ph || 'no identifiable data'}`);
        }
        if (columnMapping.lastName && !ln) {
          markRowError(index, `Row ${rowNum}: Missing last name — ${fn || em || ph || 'no identifiable data'}`);
        }

        const hasEmail = columnMapping.email && em;
        const hasPhone = columnMapping.phone && ph;

        if (!hasEmail && !hasPhone) {
          markRowError(index, `Row ${rowNum} (${ident}): Missing both email and phone`);
        }

        if (hasEmail) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(em)) {
            warnings.push(`Row ${rowNum} (${ident}): Invalid email format "${em}"`);
          }
        }
      });
    }
  }

  return { fileErrors, rowErrors, warnings, badRowIndexes };
}

/**
 * Pure record-building core (the payload the callable receives). `rows`
 * must already exclude user-skipped invalid rows. Every unmapped-or-
 * unconsumed column flows into extraFields so no data is silently dropped.
 *
 * `globalTags` are applied to EVERY record, merged (deduped) with any
 * per-row tags from a mapped Tags column. Kept as a comma-separated
 * string because both import callables parse record.tags with split(',').
 *
 * Default contact types are NOT filled into records here: the wizard's
 * selection travels as the callable's `contactTypes` param and the backend
 * falls back to it for rows without a CSV value. Keeping records free of
 * default-filled types lets overwrite mode distinguish "the file provided a
 * type" (union it into the existing contact) from "the wizard has a default"
 * (new records only — never mutates existing contacts).
 */
function buildMappedRecords({ rows, csvHeaders, columnMapping, importType, isRetrImport, retrState, globalTags = [] }) {
  const consumedHeaders = new Set(
    Object.entries(columnMapping)
      .filter(([field, header]) => header && isFieldConsumed(field, isRetrImport, importType))
      .map(([, header]) => header)
  );

  const cleanGlobalTags = (globalTags || [])
    .map(t => String(t).trim())
    .filter(Boolean);

  return rows.map(row => {
    const mapped = {};
    Object.entries(columnMapping).forEach(([field, header]) => {
      if (!header || !isFieldConsumed(field, isRetrImport, importType)) return;
      mapped[field] = row[header] || '';
    });

    // Collect unconsumed CSV columns into extraFields
    const extraFields = {};
    csvHeaders.forEach(header => {
      if (!consumedHeaders.has(header) && row[header] && String(row[header]).trim()) {
        extraFields[header] = row[header];
      }
    });
    if (Object.keys(extraFields).length > 0) {
      mapped.extraFields = extraFields;
    }

    if (isRetrImport) {
      if (mapped.loName && !mapped.firstName) {
        const { first, last } = splitFullName(mapped.loName);
        mapped.firstName = first;
        mapped.lastName = last;
      }
      delete mapped.loName;

      if (mapped.retrLoanCount) mapped.retrLoanCount = parseRetrInt(mapped.retrLoanCount);
      if (mapped.retrLoanVolume) mapped.retrLoanVolume = parseRetrCurrency(mapped.retrLoanVolume);
      if (mapped.retrConvCount) mapped.retrConvCount = parseRetrInt(mapped.retrConvCount);
      if (mapped.retrVaCount) mapped.retrVaCount = parseRetrInt(mapped.retrVaCount);
      if (mapped.retrFhaCount) mapped.retrFhaCount = parseRetrInt(mapped.retrFhaCount);
      if (mapped.retrTotalLoanCount) mapped.retrTotalLoanCount = parseRetrInt(mapped.retrTotalLoanCount);
      if (mapped.retrTotalLoanVolume) mapped.retrTotalLoanVolume = parseRetrCurrency(mapped.retrTotalLoanVolume);
      if (mapped.retrAvgLoan) mapped.retrAvgLoan = parseRetrCurrency(mapped.retrAvgLoan);
      if (mapped.retrBuyerAgentsCount) mapped.retrBuyerAgentsCount = parseRetrInt(mapped.retrBuyerAgentsCount);
      if (mapped.retrYearsExperience) mapped.retrYearsExperience = parseRetrInt(mapped.retrYearsExperience);
      if (mapped.retrStateLicensesCount) mapped.retrStateLicensesCount = parseRetrInt(mapped.retrStateLicensesCount);
      if (mapped.retrFederalRegistrationsCount) mapped.retrFederalRegistrationsCount = parseRetrInt(mapped.retrFederalRegistrationsCount);

      mapped.retrState = retrState;
    }

    if (cleanGlobalTags.length > 0) {
      const rowTags = mapped.tags
        ? String(mapped.tags).split(',').map(t => t.trim()).filter(Boolean)
        : [];
      cleanGlobalTags.forEach(tag => {
        if (!rowTags.includes(tag)) rowTags.push(tag);
      });
      mapped.tags = rowTags.join(',');
    }

    return mapped;
  });
}

module.exports = {
  RETR_SIGNATURE_HEADERS,
  RETR_EXPANDED_SIGNATURE_HEADERS,
  detectRetrFormat,
  parseRetrCurrency,
  parseRetrInt,
  splitFullName,
  CONTACT_IMPORT_CONSUMED_FIELDS,
  DEAL_IMPORT_CONSUMED_FIELDS,
  isFieldConsumed,
  FIELD_VARIATIONS,
  detectColumnMapping,
  detectImportKind,
  validateMappedData,
  buildMappedRecords,
};
